import styles from "./index.module.css";
import LoginForm from "../LoginForm";
import AgreementModal from "../AgreementModal";
import Dashboard from "../Dashboard";
import Step1 from "../Step1";
import Step2 from "../Step2";
import Step3 from "../Step3";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { RootState } from "../../store/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AlternateUser from "../AlternateUser";
import { useGetStagesQuery } from "../../services/api";
import PurlDashboard from "../PurlDashboard";
import { useNavigate } from "react-router-dom";
import TimerWidget from "../Test";
import Loader from "../Loader";
import ChatBot from "../ChatBot";
// import { hasPermission } from "../../services/hasPermission";
// import { hasRole } from "../../services/hasRole";
import useHasPermission from "../../hooks/useHasPermission";
import { nextStep, setStep } from "../../store/reducers/stepReducer";

interface Props {}

type Step = {
  step: number;
};
type Action = {
  type: string;
  payload?: number;
};

const reducer = (state: Step, action: Action) => {
  switch (action.type) {
    case "next":
      return { step: state.step + 1 };
    case "back":
      return { step: state.step - 1 };
    case "jump":
      return { step: action.payload ?? 0 };
    default:
      return { step: 0 };
  }
};

const BlackLayout = (props: Props) => {
  const { hasPermission } = useHasPermission();
  const {
    steps: { step },
    permission,
    stage: {
      videoComplete,
      purlLoginComplete,
      presentationComplete,
      benefitCalculatorComplete,
      purlNdaChecked,
    },
    login: { role },
  } = useAppSelector((state) => state);

  // console.log("Yes values are set the values are: ",userFlow)

  let component;
  let customClass;
  const [table, setTable] = useState<FormResponse>();
  const [isSolarId, setIsSolarId] = useState(false);
  const [solarId, setSolarId] = useState("");
  const [loading, setLoading] = useState(true);

  const id = table?._id as string;
  if (step === 0) {
    customClass = "h-screen";
  } else if (step === 5 || step === 3 || step === 2 || step === 1) {
    customClass = "h-screen sm:min-h-screen";
  }

  const navigate = useNavigate();
  const roles = {
    customer: role == "CUSTOMER",
    advisor: role == "ADVISOR",
    cpa: role == "CPA",
    bookkeeper: role == "BOOKKEEPER",
  };

  const isLoggedIn = localStorage.getItem("token");
  const isStagesComplete =
    isLoggedIn &&
    videoComplete &&
    purlLoginComplete &&
    presentationComplete &&
    benefitCalculatorComplete;
  const isNewDashboard = !hasPermission("dashboard");
  const showTimeTracker =
    (step != 0 || localStorage.getItem("token")) &&
    !!hasPermission("solarTracker");

  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const isDashboardView = isStagesComplete && currentStep === 5 && !loading;
  const showChatBot = !!hasPermission("chatBot") && isDashboardView;

  const isInitialRender = useRef(true);

  // Determine the initial step in case of the reload
  // Redirect to the step which is in the localstorage
  const getInitialStep = useCallback(() => {
    const savedStep = localStorage.getItem("currentStep");
    if (savedStep) {
      return parseInt(savedStep, 10);
    }
    return determineInitialStep();
  }, []);
  //Determine the  step with the help of the conditions
  const determineInitialStep = useCallback(() => {
    if (!isLoggedIn) return 0;

    if (purlLoginComplete && purlNdaChecked == null) {
      return 1;
    }

    const completedAllPermittedSteps =
      videoComplete &&
      presentationComplete &&
      benefitCalculatorComplete &&
      purlLoginComplete;

    if (completedAllPermittedSteps) {
      return 5;
    }

    if (!videoComplete) return 2;
    if (!presentationComplete) return 3;
    if (!benefitCalculatorComplete) return 4;

    return 0;
  }, [
    isLoggedIn,
    purlLoginComplete,
    purlNdaChecked,
    videoComplete,
    presentationComplete,
    benefitCalculatorComplete,
  ]);

  // Set the initial step on the first render{on reload or on first time}
  useEffect(() => {
    if (isInitialRender.current) {
      const initialStep = getInitialStep();
      dispatch(setStep(initialStep));
      setCurrentStep(initialStep);
      isInitialRender.current = false;
      setLoading(false);
    }
  }, [getInitialStep, dispatch]);

  // Set the current step on change or move from  one step to another
  useEffect(() => {
    const token = localStorage.getItem("token");
    //To check if user is logged in
    if (!token) {
      dispatch(setStep(0));
      setCurrentStep(0);
      localStorage.setItem("currentStep", "0");
      setLoading(false);
      return;
    }
    if (isInitialRender.current) {
      return;
    }
    const prevStep = localStorage.getItem("currentStep");
    //Localstorage update only if the current step is greater than the previous step
    if ((isLoggedIn && step > parseInt(prevStep ?? "0", 10)) || !prevStep) {
      localStorage.setItem("currentStep", step.toString());
    }
    if (step !== currentStep) {
      setCurrentStep(step);
      setLoading(false);
    }
  }, [isLoggedIn, step, currentStep, dispatch]);

  if (isStagesComplete) {
    component = isNewDashboard ? <PurlDashboard /> : <Dashboard />;
  } else {
    switch (currentStep) {
      case 0:
        component = isLoggedIn ? (
          isNewDashboard ? (
            <PurlDashboard />
          ) : (
            <Dashboard />
          )
        ) : (
          <LoginForm setIsSolarId={setIsSolarId} />
        );
        break;
      case 1:
        component = <AgreementModal />;
        break;
      case 2:
        component = <Step1 />;
        break;
      case 3:
        component = <Step2 />;
        break;
      case 4:
        component = <Step3 setTable={setTable} />;
        break;
      case 5:
        if (!isLoggedIn) navigate("/");
        component = isNewDashboard ? <PurlDashboard /> : <Dashboard />;
        break;
      default:
        component = <AlternateUser />;
    }
  }

  useEffect(() => {
    const solarId = localStorage.getItem("solarId") || "";
    setSolarId(solarId);
  }, [isSolarId]);

  if (loading) {
    return (
      <div className="h-dvh flex justify-center items-center">
        <Loader isLoading={true} size={20} />
      </div>
    );
  }

  const isHide = !isStagesComplete && (step >= 1 && step <= 4);

  return (
    <>
      <div className={styles.customClass}>
        <div className={styles.content}>{component}</div>
      </div>
      {showTimeTracker && <TimerWidget userId={solarId} isHide={isHide} />}
      {showChatBot && <ChatBot />}
    </>
  );
};

export default BlackLayout;
